import { Component, OnDestroy, OnInit } from '@angular/core';
import {
  faAngleDown,
  faAngleUp,
  faExchangeAlt,
  faSignOut,
  faUser,
  faUserFriends,
} from '@fortawesome/pro-regular-svg-icons';
import { BenutzermanagementAPIService } from '../../api/benutzermanagement/benutzermanagement-api.service';
import { AppToastService } from '../../toast/app-toast.service';
import { faSpinner } from '@fortawesome/pro-solid-svg-icons';
import { Router } from '@angular/router';
import { ManagedAccount } from '../../api/benutzermanagement/models';
import { ManagedAccountService } from '../../api/managed-account/managed-account.service';
import { Store } from '@ngrx/store';
import {
  ProductsLoadedAction,
  SetAuthenticatedAction,
} from '../../reducers/authinfo.reducer';
import { AccountSwitchLoadingService } from '../../shared/account-switch-loading/account-switch-loading.service';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'san-account-button',
  templateUrl: './account-button.component.html',
  styleUrls: ['./account-button.component.scss'],
  standalone: false,
})
export class AccountButtonComponent implements OnInit, OnDestroy {
  userIcon = faUser;
  pfeilIcon = faAngleDown;
  pfeilHochIcon = faAngleUp;
  logoutIcon = faSignOut;
  switchIcon = faExchangeAlt;
  managedAccountsIcon = faUserFriends;
  out = false;
  userdata = {
    vorname: 'wird geladen ...',
    nachname: 'wird geladen ...',
    kundennummer: 'wird geladen ...',
    email: 'wird geladen ...',
  };
  loggingOut = false;
  loadingIcon = faSpinner;

  private destroy$ = new Subject<void>();

  // List of managed accounts from API
  managedAccounts: ManagedAccount[] = [];

  // Flag to show/hide managed accounts section
  showManagedAccounts = false;

  constructor(
    private bmClient: BenutzermanagementAPIService,
    private toastService: AppToastService,
    private router: Router,
    private managedAccountService: ManagedAccountService,
    private store: Store,
    private accountSwitchLoadingService: AccountSwitchLoadingService,
  ) {}

  ngOnInit(): void {
    this.bmClient
      .getUserdata(1, 100, null, null, [
        'Sanakey/Kundennummer',
        'Sanakey/Vorname',
        'Sanakey/Nachname',
        'Sanakey/Standard E-Mail',
      ])
      .pipe(takeUntil(this.destroy$))
      .subscribe((ediPage) => {
        for (const edi of ediPage.results) {
          if (!(typeof edi.value === 'string')) {
            continue;
          }
          const key = `${edi.extradata.namespace}/${edi.extradata.name}`;
          switch (key) {
            case 'Sanakey/Kundennummer':
              this.userdata.kundennummer = edi.value;
              break;
            case 'Sanakey/Vorname':
              this.userdata.vorname = edi.value;
              break;
            case 'Sanakey/Nachname':
              this.userdata.nachname = edi.value;
              break;
            case 'Sanakey/Standard E-Mail':
              this.userdata.email = edi.value;
              break;
          }
        }
        this.loadManagedAccounts();
      });
    this.managedAccountService.reloadManagedAccounts$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.loadManagedAccounts();
      });

    this.managedAccountService
      .getActiveAccount()
      .pipe(takeUntil(this.destroy$))
      .subscribe((uuid) => {
        this.updateActiveAccountInList(uuid);
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Load managed accounts from the API
   */
  loadManagedAccounts(): void {
    this.bmClient
      .getManagedAccounts()
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (accounts) => {
          this.managedAccounts = accounts || [];

          // Synchronize active state with ManagedAccountService
          const activeUuid =
            this.managedAccountService.getCurrentActiveAccount();
          this.updateActiveAccountInList(activeUuid);

          // Schließe ggf. geöffnete Menüs, falls keine Accounts vorhanden sind
          if (!this.hasManagedAccounts()) {
            this.showManagedAccounts = false;
            this.out = false;
          }
        },
        (error) => {
          console.error('Error fetching managed accounts:', error);
          this.toastService.show(
            'Fehler',
            'Die verwalteten Accounts konnten nicht geladen werden.',
            'danger',
          );

          this.managedAccounts = [];
          this.showManagedAccounts = false;
          this.out = false;
        },
      );
  }

  private updateActiveAccountInList(uuid: string | null): void {
    if (!this.managedAccounts) {
      return;
    }
    this.managedAccounts.forEach((acc) => {
      acc.active = acc.uuid === uuid;
    });
  }

  hasManagedAccounts() {
    return (this.managedAccounts?.length || 0) > 0;
  }

  logout() {
    this.loggingOut = true;
    this.bmClient.logOut().subscribe(
      () => {
        this.loggingOut = false;
        this.router.navigate(['/']);
        this.toastService.show(
          'Erfolgreich abgemeldet',
          'Sie wurden abgemeldet',
          'success',
        );
      },
      () => {
        this.loggingOut = false;
        this.toastService.show(
          'Nicht abgemeldet',
          'Sie konnten nicht abgemeldet werden. Bitte versuchen Sie es später erneut',
          'danger',
        );
      },
    );
  }

  toggleAccountMenu() {
    this.out = !this.out;
    if (this.out) {
      this.showManagedAccounts = false;
    }
  }

  /**
   * Toggle the visibility of managed accounts section
   */
  toggleManagedAccounts() {
    this.showManagedAccounts = !this.showManagedAccounts;
    if (this.showManagedAccounts) {
      this.out = false;
    }
  }

  /**
   * Switch to a managed account
   * @param account The account to switch to
   */
  switchToAccount(account: ManagedAccount) {
    // Reset active state for all accounts
    this.managedAccounts.forEach((acc) => (acc.active = false));

    // Set the selected account as active
    const selectedAccount = this.managedAccounts.find(
      (acc) => acc.uuid === account.uuid,
    );
    if (selectedAccount) {
      selectedAccount.active = true;

      // Set the active managed account UUID in the service
      this.managedAccountService.setActiveAccount(selectedAccount.uuid);

      // Show the account switch loading animation
      this.accountSwitchLoadingService.showLoading();

      // Dispatch SetAuthenticatedAction with the managed account's username and the current device_id
      // This will trigger the same actions that occur after login
      this.store.dispatch(new ProductsLoadedAction([]));
      this.store.dispatch(
        new SetAuthenticatedAction(selectedAccount.kundennummer, 0),
      );

      // Show success message
      this.toastService.show(
        'Account gewechselt',
        `Sie sind jetzt als ${selectedAccount.vorname} ${selectedAccount.nachname} angemeldet`,
        'success',
      );

      // Close the dropdown
      this.out = false;
      this.showManagedAccounts = false;

      // Reload the dashboard to apply the new header to all requests
      this.router.navigate(['/dashboard']);
    }
  }

  /**
   * Return to the Verwalter-Account
   */
  returnToVerwalterAccount() {
    // Reset active state for all accounts
    this.managedAccounts.forEach((acc) => (acc.active = false));

    // Clear the active managed account UUID in the service
    this.managedAccountService.clearActiveAccount();

    // Show the account switch loading animation
    this.accountSwitchLoadingService.showLoading();

    // Dispatch SetAuthenticatedAction with the Verwalter-Account's username and the current device_id
    // This will trigger the same actions that occur after login
    this.store.dispatch(new ProductsLoadedAction([]));
    this.store.dispatch(
      new SetAuthenticatedAction(this.userdata.kundennummer, 0),
    );

    // Show success message
    this.toastService.show(
      'Zurück zum Verwalter-Account',
      'Sie sind jetzt wieder mit Ihrem Verwalter-Account angemeldet',
      'success',
    );

    // Close the dropdown
    this.out = false;
    this.showManagedAccounts = false;

    // Reload the dashboard to apply the changes
    this.router.navigate(['/dashboard']);
  }

  /**
   * Check if any managed account is currently active
   */
  isManagingAccount() {
    return this.managedAccounts.some((acc) => acc.active);
  }

  /**
   * Get the active managed account
   */
  getActiveAccount() {
    return this.managedAccounts.find((acc) => acc.active);
  }

  /**
   * Get the active account's first name
   */
  getActiveAccountVorname() {
    const activeAccount = this.getActiveAccount();
    return activeAccount ? activeAccount.vorname : '';
  }

  /**
   * Get the active account's last name
   */
  getActiveAccountNachname() {
    const activeAccount = this.getActiveAccount();
    return activeAccount ? activeAccount.nachname : '';
  }

  /**
   * Get the active account's customer number
   */
  getActiveAccountKundennummer() {
    const activeAccount = this.getActiveAccount();
    return activeAccount ? activeAccount.kundennummer : '';
  }

  /**
   * Get the active account's customer number
   */
  getActiveAccountNameDerInstitution() {
    const activeAccount = this.getActiveAccount();
    return activeAccount ? activeAccount.name_der_institution : '';
  }

  /**
   * Get the active account's email
   */
  getActiveAccountEmail() {
    const activeAccount = this.getActiveAccount();
    return activeAccount ? activeAccount.email : '';
  }
}
